import { useCallback, useState } from "react";
import { ApiService } from "../../../api";
import CoverageDocumentService from "../../../api/services/operation/coverage-document-service";
import CoveragePhaseService from "../../../api/services/operation/coverage-phase-service";
import ProjectTeamService from "../../../api/services/project/project-team-service";
import CategoryService from "../../../api/services/category-service";
import { updateTreeBranch } from "../../../common/utils";

/**
 * Normalizes user data to a consistent format
 * @param {Array} users - Raw user data
 * @returns {Array} Normalized user objects
 */
const normalizeUsers = (users) => {
	const arr = users?.contents || users?.content || users || [];
	return arr.map((u) => ({
		...u,
		id: u.id,
		displayName: u?.displayName || [u?.firstName, u?.lastName].filter(Boolean).join(" "),
	}));
};

/**
 * Updates selected deliverable with matching phase and owner from loaded options
 * @param {Object} deliverable - The deliverable to update
 * @param {Function} setSelectedDeliverable - State setter function
 * @param {Array} loadedPhases - Available phases
 * @param {Array} normalizedOwners - Available owners
 */
function updateSelectedDeliverableWithMatches(deliverable, setSelectedDeliverable, loadedPhases, normalizedOwners) {
	const desiredPhaseId = deliverable.phase?.id || deliverable.phaseId;
	const desiredPhaseName = deliverable.phase?.name || deliverable.phaseName;
	const desiredOwnerId = deliverable.owner?.id || deliverable.ownerId;
	const desiredOwnerName = deliverable.owner?.displayName || deliverable.ownerName || deliverable.owner?.name;

	const matchingPhase = loadedPhases.find((p) => p.id === desiredPhaseId || p.name === desiredPhaseName);
	const matchingOwner = normalizedOwners.find(
		(o) => o.id === desiredOwnerId || o.displayName === desiredOwnerName || o.name === desiredOwnerName
	);

	setSelectedDeliverable((prev) => ({
		...prev,
		phase: matchingPhase || prev?.phase || null,
		phaseId: matchingPhase?.id || prev?.phaseId || "",
		owner: matchingOwner || prev?.owner || null,
		ownerId: matchingOwner?.id || prev?.ownerId || "",
	}));
}

/**
 * Hook for managing dialog options (types, formats, phases, owners, fbs)
 * @param {string} projectId - Current project ID
 * @param {Object} isMountedRef - Ref to track component mount state
 * @returns {Object} Dialog options state and handlers
 */
const useDialogOptions = (projectId, isMountedRef) => {
	const [dialogOptions, setDialogOptions] = useState({
		types: [],
		formats: [],
		phases: [],
		owners: [],
		fbs: [],
	});

	const loadDialogOptions = useCallback(
		async (sidePanelMode, selectedDeliverable, setSelectedDeliverable) => {
			if (!projectId || !isMountedRef?.current) {
				return;
			}

			const cancelTokenSource = ApiService.getCancelTokenSource();

			try {
				const [types, formats, phases, owners, fbs] = await Promise.all([
					CoverageDocumentService.getTypes(cancelTokenSource.token),
					CoverageDocumentService.getFormats(cancelTokenSource.token),
					CoveragePhaseService.getAllByProject({ projectId }, cancelTokenSource.token),
					ProjectTeamService.searchUsersInProjectAndCompany({ projectId }, "", cancelTokenSource.token),
					CategoryService.getTreeParentsForProject({ projectId, details: false }, cancelTokenSource.token),
				]);

				if (!isMountedRef?.current) {
					return;
				}

				const normalizedOwners = normalizeUsers(owners);
				const loadedPhases = phases?.content || phases?.contents || phases || [];

				setDialogOptions({
					types: types || [],
					formats: formats || [],
					phases: loadedPhases,
					owners: normalizedOwners,
					fbs: fbs || [],
				});

				if (sidePanelMode === "edit" && selectedDeliverable && setSelectedDeliverable) {
					updateSelectedDeliverableWithMatches(
						selectedDeliverable,
						setSelectedDeliverable,
						loadedPhases,
						normalizedOwners
					);
				}
			} catch (error) {
				console.error("Error loading dialog options:", error);
			}
		},
		[projectId, isMountedRef]
	);

	const handleExpandCategoryRow = useCallback(
		async (category) => {
			if (Array.isArray(category.subThematic) && category.subThematic.length > 0) {
				return;
			}

			const cancelTokenSource = ApiService.getCancelTokenSource();
			try {
				const data = await CategoryService.getTreeChildrenForProject(
					{ parentId: category.id, details: false },
					cancelTokenSource.token
				);

				if (!isMountedRef?.current) {
					return;
				}

				setDialogOptions((prev) => ({
					...prev,
					fbs: updateTreeBranch({
						tree: prev.fbs,
						idToReplace: category.id,
						newBranch: {
							...category,
							subThematic: data,
						},
						iterativeBranchName: "subThematic",
					}),
				}));
			} catch (error) {
				console.error("Error expanding category:", error);
			}
		},
		[isMountedRef]
	);

	return {
		dialogOptions,
		loadDialogOptions,
		handleExpandCategoryRow,
	};
};

export default useDialogOptions;
